import {Component, OnInit, ViewEncapsulation} from '@angular/core';

import {
  MAT_PAGINATOR_DEFAULT_OPTIONS,
  MatPaginatorDefaultOptions
} from '@angular/material/paginator';
import {ActivatedRoute, NavigationExtras, Router} from '@angular/router';
import {Actions} from '@ngneat/effects-ng';
import {TranslateService} from '@ngx-translate/core';
import {
  ConfigService,
  OrgInfo,
  PermissionsService,
  REF_MODEL_STORE,
  REPORT_MODAL_TYPE,
  RefModelStoreService,
  Refmodel,
  SnackBarService,
  TakeUntilComponentDestroy
} from '@q9elements/ui-kit/common';
import {GridQuery, GridStore, filterEntities, loadEntities} from '@q9elements/ui-kit/components';
import {Observable, of, throwError} from 'rxjs';
import {catchError, take} from 'rxjs/operators';
import {RmeNodeDaoAbstractService} from '../core/abstract/rme-node-dao-abstract.service';
import {GridDataSourceService} from '../custom-grid/services/grid-data-source.service';
import {CustomViewService} from '../editor-interface/services/custom-view.service';
import {RefModelService} from '../services/ref-model.service';
import {SfApiService} from '../services/sf-api.service';
import {ReportDialogService} from '../shared/services/report-dialog.service';
import {ShareRefModelDialogService} from '../shared/services/share-refModel-dialog.service';
import {CodeSearchStoreEnum} from './models/enum/code-search-store.enum';
import {CodeSearchTypesEnum} from './models/enum/code-search-types.enum';
import {CodeSearchDaoService} from './services/code-search-dao.service';
import {CodeSearchFirebaseService} from './services/code-search-firebase.service';
import {CodeSearchStoreService} from './services/code-search-store.service';
import {CodeSearchQuery} from './state/code-search.query';
import {CodeSearchStore} from './state/code-search.store';

const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 100;

@Component({
  selector: 'app-code-search',
  templateUrl: './code-search.component.html',
  styleUrls: ['./code-search.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {provide: GridStore, useClass: CodeSearchStore},
    {provide: GridQuery, useClass: CodeSearchQuery},
    {
      provide: MAT_PAGINATOR_DEFAULT_OPTIONS,
      useValue: {
        pageSize: DEFAULT_PAGE_SIZE,
        pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS
      } as MatPaginatorDefaultOptions
    }
  ]
})
export class CodeSearchComponent extends TakeUntilComponentDestroy implements OnInit {
  public isLoading = false;
  public title = '';
  public subTitle: string[] = [];
  public filters = this.gridQuery.selectFilters();
  public errorMessage: string | null = null;
  public showShareRefModelButton = this.rmeStore.canShareRefModel;

  private refModelId: string = this.rmeStore.refModel._id;

  protected refModel$: Observable<Refmodel> = this.rmeStore.select(REF_MODEL_STORE.REF_MODEL);
  protected orgInfo$: Observable<OrgInfo> = this.rmeStore.select(REF_MODEL_STORE.ORG_INFO);

  constructor(
    private readonly rmeStore: RefModelStoreService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly codeSearchDaoService: CodeSearchDaoService,
    private readonly codeSearchStoreService: CodeSearchStoreService,
    private readonly codeSearchFirebaseService: CodeSearchFirebaseService,
    private readonly sfService: SfApiService,
    private readonly translate: TranslateService,
    private readonly gridDataSourceService: GridDataSourceService,
    private readonly snackBarService: SnackBarService,
    private readonly nodeService: RmeNodeDaoAbstractService,
    private readonly refModelService: RefModelService,
    private readonly permissionsService: PermissionsService,
    private customViewService: CustomViewService,
    private reportDialogService: ReportDialogService,
    private gridQuery: GridQuery,
    private actions: Actions,
    private shareRefModelDialogService: ShareRefModelDialogService
  ) {
    super();
  }

  public ngOnInit() {
    this.route.queryParams.pipe(this.takeUntilComponentDestroy()).subscribe(queryParams => {
      const {reload} = queryParams;
      if (reload) {
        this.loadData();
        const currentParams = {...this.route.snapshot.queryParams};
        delete currentParams['reload'];
      }
    });

    this.loadData();
  }

  private checkQueryParams(queryParams: any): void {
    const rmeAppUrl = `refmodel/${this.refModelId}`;
    const {type, search} = queryParams;

    if (!Object.values(CodeSearchTypesEnum).includes(type)) {
      this.router.navigate([rmeAppUrl]);
    }

    if (type === CodeSearchTypesEnum.TEXT && !search) {
      this.router.navigate([rmeAppUrl]);
    }
  }

  private loadData(): void {
    this.route.params.pipe(take(1)).subscribe(params => {
      const id = params['refModelId'];
      this.refModelId = id;
      this.sfService.getSFOrgInfoDataByRefmodel(this.refModelId).subscribe();
      this.refModelService.getOptimizeStatuses().subscribe();

      this.route.queryParams.pipe(take(1)).subscribe(queryParams => {
        this.checkQueryParams(queryParams);

        const {rmn, refModelNode, type, search, metadataTypes} = queryParams;
        const node = refModelNode || rmn;
        const parsedMetadataTypes = metadataTypes ? JSON.parse(metadataTypes) : undefined;
        this.loadDataInStore({rmn: node, type, search, metadataTypes: parsedMetadataTypes});
        this.composeTitle(search);
        this.loadSearchResults(id, node, type, search, parsedMetadataTypes);
      });
    });
  }

  private saveParamsToStore(
    id: string,
    node: string,
    type: string,
    search: string,
    metadataTypes?: string[]
  ): void {
    this.gridQuery.store.update(state => ({
      ...state,
      payload: {
        refModel: id,
        refModelNode: node,
        type: type,
        search: search,
        metadataTypes: metadataTypes,
        sort: null,
        skip: 0,
        limit: 100
      }
    }));
  }

  private loadSearchResults(
    rmId: string,
    rmn: string,
    type: string,
    search: string,
    metadataTypes?: string[]
  ) {
    this.saveParamsToStore(rmId, rmn, type, search, metadataTypes);

    this.gridDataSourceService.loading = true;

    const payload = {
      refModel: rmId,
      refModelNode: rmn,
      type,
      search,
      sort: null,
      skip: 0,
      limit: 100
    };

    const body: any = {filters: []};

    this.codeSearchDaoService
      .getSearchResults(payload, body)
      .pipe(
        this.takeUntilComponentDestroy(),
        catchError(err => {
          if (err.status === 400) {
            switch (err?.error?.status) {
              case 'code_search.err.in_progress':
                return of({reload: true});
              case 'code_search.err.sync_in_progress':
                return of({syncActive: true});
              case 'err.rights.enterprise_not_active':
                this.errorMessage = err.error.error;
                break;
              case 'code_search.err.code_search_should_be_initialized':
              case 'code_search.err.xml_uploading_in_progress':
              case 'code_search.err.xml_uploading_failed':
                return of({locked: true, err: err?.error?.error});
              default:
                return throwError(err);
            }
          }
          return throwError(err);
        })
      )
      .subscribe(result => {
        const queryParams = {
          refModel: rmId,
          refModelNode: rmn,
          type,
          search
        };

        if (result.syncActive === true) {
          const rmeAppUrl = `refmodel/${this.refModelId}`;
          this.router.navigate([rmeAppUrl]).then(() => {
            this.snackBarService.info('CODE_SEARCH.SYNC_IN_PROGRESS');
          });
          return;
        }

        if (result.locked === true) {
          const rmeAppUrl = `refmodel/${this.refModelId}`;
          this.router.navigate([rmeAppUrl]).then(() => {
            this.snackBarService.error(result.err);
          });
          return;
        }

        if (result.reload === true) {
          this.codeSearchStoreService.storeFirebaseLoadingKey(rmId, type);
          // this.codeSearchFirebaseService.emptyCodeSearchState().then(() => {
          this.navigateToLoading(queryParams);
          // });
        } else {
          delete queryParams['refModel'];

          this.subTitle = (result.metaDataTypesWhereFound || []).join(', ');
          this.isLoading = false;
          this.codeSearchStoreService.updateData(result);
          this.codeSearchStoreService.updateSubject(
            CodeSearchStoreEnum.CODE_SEARCH_WHERE_TYPES$,
            this.subTitle
          );
          this.codeSearchStoreService.updateSubject(
            CodeSearchStoreEnum.CODE_SEARCH_TOTAL$,
            result.total
          );
          this.router.navigate(['code-search', this.refModelId, 'result'], {
            queryParams
          });
        }
      });
  }

  private navigateToLoading(queryParams: any): void {
    const navigationExtras: NavigationExtras = {queryParams};
    this.isLoading = true;
    this.router.navigate(['code-search', this.refModelId, 'loading'], navigationExtras);
  }

  private composeTitle(search?: string) {
    switch (this.codeSearchStoreService.getCodeSearchType()) {
      case CodeSearchTypesEnum.IDS:
        this.title = this.translate.instant('CODE_SEARCH.SEARCH_IDS_TITLE_2');
        break;
      case CodeSearchTypesEnum.TEXT:
        this.title = this.translate.instant('CODE_SEARCH.SEARCH_TEXT_TITLE_2', {search});
        break;
      case CodeSearchTypesEnum.NODE:
        this.nodeService.getNode(this.refModelId, search).subscribe(node => {
          this.title = this.translate.instant('CODE_SEARCH.SEARCH_NODE_REF_TITLE_2', {
            labelOfNode: node.name
          });
        });
        break;
    }
  }

  public copyLink() {
    window.navigator.clipboard.writeText(window.location.href);
    this.snackBarService.success('CODE_SEARCH.LINK_COPIED_NOTIFICATION');
  }

  private loadDataInStore({rmn, type, search, metadataTypes}) {
    this.codeSearchStoreService.set(CodeSearchStoreEnum.CODE_SEARCH_TYPE, type);
    this.codeSearchStoreService.set(CodeSearchStoreEnum.CODE_SEARCH_QUERY, search);
    this.codeSearchStoreService.set(CodeSearchStoreEnum.CODE_SEARCH_RMN, rmn);
    this.codeSearchStoreService.set(CodeSearchStoreEnum.CODE_SEARCH_METADATA_TYPES, metadataTypes);
  }

  public reloadGrid() {
    this.actions.dispatch(loadEntities());
  }

  public resetFilters() {
    this.actions.dispatch(filterEntities({filters: []}));
  }

  goToRefModelView(): void {
    const url = `${ConfigService.environment.REF_MODEL_APP}/refmodel/${this.refModelId}`;

    window.open(url, this.rmeStore.anchorTarget);
  }

  public showSavedReports(modalType: REPORT_MODAL_TYPE): void {
    const currentView = this.customViewService.selectedView;

    this.reportDialogService.openReportDialog(
      modalType,
      this.rmeStore.selectedNode,
      this.rmeStore.refModel,
      this.rmeStore.rootData,
      currentView
    );
  }

  public onShareRefModel() {
    this.shareRefModelDialogService.openRefModelShareDialog();
  }
}
